import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Bin,
  BreadcrumbHeading,
  Chip,
  EditIcon,
  Export,
  Plus,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  SoftButton,
  Spinner,
  TickIcon,
  Typography,
  useSnackbar,
  ValidationIcon,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { SpinnerIcon } from 'src/components/SpinnerIcon';
import { DeleteEntityPrompts } from 'src/components/DeleteEntityPrompts';
import { MenuSetMenu } from 'src/components/MenuSetMenu';
import MenuSetPublishModal from 'src/components/MenuSetPublishModal';
import { ValidationTrayContent, getValidationTrayTitle } from 'src/components/ValidationTray';
import { ValidationReportContainer } from 'src/components/ValidationReportContainer';
import { AddMenusToMenuSetModal } from 'src/components/AddMenusToMenuSetModal';
import { MenuSetDetailsTrayContent } from 'src/components/MenuSetDetailsTray';
import { MenuTrayProvider, useMenuTray } from 'src/contexts/menuTray';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { formatTime } from 'src/helpers/formatTime/formatTime';
import { ValidationStatus } from 'src/api/getSchedules/types';
import { VALIDATION_POLL_INTERVAL_MS } from 'src/hooks/useSchedules/useSchedules';
import { getNextOpenId } from 'src/helpers/getNextOpenId';
import { useMenuSet } from 'src/hooks/useMenuSet';
import { useDeleteMenuSetPrompt } from 'src/hooks/useDeleteMenuSetPrompt';
import { useUpdateMenuSet } from 'src/hooks/useUpdateMenuSet';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import styles from './MenuSet.module.scss';

const DELETE_ERROR_MESSAGE = 'Something went wrong whilst deleting the menu set';

export interface IMenuSetLocationState {
  openDetailsTray?: boolean;
}

export const MenuSet = () => (
  <MenuTrayProvider>
    <MenuSetContent />
  </MenuTrayProvider>
);

const MenuSetContent = () => {
  const { menuSetId } = useParams<{ menuSetId?: string }>();
  // Poll while a validation runs so Validate and View Validation Report update when it finishes
  const { data, isLoading, isRefetchError } = useMenuSet(menuSetId, {
    refetchInterval: (response) =>
      response?.data[0]?.validation?.status === ValidationStatus.VALIDATING ? VALIDATION_POLL_INTERVAL_MS : false,
  });
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const menuSet = data?.data[0];
  const navigate = useNavigate();
  const location = useLocation();
  const { openTray, closeTray, isTrayOpen, setTrayDirty } = useMenuTray();
  const [publishModal, setPublishModal] = useState<{ isTrial: boolean } | null>(null);
  const [isAddMenusOpen, setIsAddMenusOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { mutateAsync: updateMenuSet, isLoading: isUpdating } = useUpdateMenuSet(menuSetId ?? '');
  const isEditingLocked = isUpdating || isRefetchError;

  const deletePrompt = useDeleteMenuSetPrompt({
    menuSetId: menuSetId ?? '',
    menuSetName: menuSet?.name,
    genericErrorMessage: DELETE_ERROR_MESSAGE,
    onDeleted: () => navigate(Routes.MENU_SETS),
  });

  const openDetailsTray = () => {
    if (!menuSetId) return;
    openTray(
      'edit-menu-set-details',
      <MenuSetDetailsTrayContent
        openId={getNextOpenId()}
        menuSetId={menuSetId}
        onClose={closeTray}
        onDirtyChange={setTrayDirty}
      />,
      { title: 'Edit details' },
    );
  };

  const openValidationTray = () => {
    if (!menuSet) return;
    openTray(
      'validate',
      <ValidationTrayContent
        key={getNextOpenId()}
        menuSetId={menuSet.menu_set_id}
        name={menuSet.name}
        onClose={closeTray}
      />,
      { title: getValidationTrayTitle(true) },
    );
  };

  // Covers navigating away from the page while the tray is still open.
  useEffect(() => closeTray, []);

  // "Edit" on the menu sets list navigates here with this state so the details tray opens straight away.
  const shouldOpenDetailsTray = (location.state as IMenuSetLocationState | null)?.openDetailsTray;
  const hasAutoOpenedDetailsTrayRef = useRef(false);
  useEffect(() => {
    if (!shouldOpenDetailsTray || !menuSet || hasAutoOpenedDetailsTrayRef.current) return;
    hasAutoOpenedDetailsTrayRef.current = true;
    // Cleared straight from browser history so a refresh doesn't reopen the tray. Clearing it with
    // navigate() would change location.key and remount the page, closing the tray again (see Timetable).
    window.history.replaceState({ ...window.history.state, usr: null }, '');
    openDetailsTray();
  }, [shouldOpenDetailsTray, menuSet]);

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  if (!menuSetId || !menuSet) return <Navigate to={Routes.MENU_SETS} replace />;

  const menuSetPath = `${Routes.MENU_SETS}/${menuSet.menu_set_id}`;
  const publicationStatus = menuSet.publication?.status ?? 'UNPUBLISHED';
  const menus = [...menuSet.menus].sort((a, b) => a.display_order - b.display_order);
  const menuIds = menus.map(({ menu_id }) => menu_id);
  const validationStatus = menuSet.validation?.status;
  const validationReportId = menuSet.validation?.report_id ?? null;
  const validatedAt = menuSet.validation?.validated_at;
  const lastValidated = validatedAt
    ? ` • Last validated - ${formatDate(new Date(validatedAt))}, ${formatTime(new Date(validatedAt))}`
    : '';

  const addMenus = async (newMenuIds: string[]) => {
    try {
      await updateMenuSet({ menus: JSON.stringify([...menuIds, ...newMenuIds]) });
      setIsAddMenusOpen(false);
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully added ${newMenuIds.length} menu(s) to ${menuSet.name}.`,
      });
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst adding the menu(s).' });
    }
  };

  const optionMenuItems = [
    {
      id: 'edit-details',
      name: 'Edit menu set details',
      onOptionClick: openDetailsTray,
      icon: EditIcon,
    },
    { id: 'divider-edit', isDivider: true as const },
    {
      id: 'publish-trial',
      name: 'Publish to trial venues',
      onOptionClick: () => setPublishModal({ isTrial: true }),
      icon: Export,
    },
    {
      id: 'view-validation-report',
      name: 'View Validation Report',
      onOptionClick: () => setIsReportModalOpen(true),
      isDisabled: !validationReportId || validationStatus !== ValidationStatus.VALIDATION_FAILED,
      icon: ValidationIcon,
    },
    {
      id: 'publish-status',
      name: 'View last publish status',
      onOptionClick: () => navigate(`${menuSetPath}/status`),
      isDisabled: publicationStatus === 'UNPUBLISHED',
      icon: TickIcon,
    },
    {
      id: 'delete-menu-set',
      name: 'Delete',
      icon: deletePrompt.isCheckingCanDelete ? SpinnerIcon : Bin,
      isDisabled: deletePrompt.isCheckingCanDelete,
      persistMenuOnClick: true,
      hoverColour: 'tomato' as const,
      onOptionClick: deletePrompt.openDeletePrompt,
    },
  ];

  return (
    <div className={styles['page-container']}>
      <BreadcrumbHeading
        breadcrumbs={[
          { path: Routes.MENU_SETS, label: 'Menu Sets' },
          { path: menuSetPath, label: menuSet.name },
        ]}
        subtitle={`Last updated at ${formatDate(new Date(menuSet.update_date))}${lastValidated}`}
        // Hidden while a tray is open so its actions can't conflict with the tray's form.
        primaryButton={
          isTrayOpen ? undefined : (
            <PrimaryButton onClick={() => setPublishModal({ isTrial: false })} icon={Export}>
              Publish
            </PrimaryButton>
          )
        }
        secondaryButton={
          isTrayOpen ? undefined : (
            <SecondaryButton
              onClick={openValidationTray}
              icon={ValidationIcon}
              disabled={validationStatus === ValidationStatus.VALIDATING}
            >
              Validate
            </SecondaryButton>
          )
        }
        optionMenuItems={isTrayOpen ? [] : optionMenuItems}
      />
      {validationReportId && (
        <ValidationReportContainer
          isOpen={isReportModalOpen}
          closeModal={() => setIsReportModalOpen(false)}
          reportId={validationReportId}
        />
      )}
      {publishModal && (
        <MenuSetPublishModal
          isOpen
          menuSetId={menuSet.menu_set_id}
          isTrial={publishModal.isTrial}
          closeModal={() => setPublishModal(null)}
        />
      )}
      {rangesEnabled && (
        <div className={styles['ranges']}>
          <Typography size="small" className={styles['label']}>
            {menuSet.ranges.length === 0 ? 'No Ranges' : 'Range(s) -'}
          </Typography>
          {menuSet.ranges.map((range) => (
            <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
          ))}
        </div>
      )}
      <SoftButton
        colour="blueberry"
        icon={Plus}
        size="full-width"
        className={styles['add-menu']}
        onClick={() => setIsAddMenusOpen(true)}
        disabled={isEditingLocked || isTrayOpen}
      >
        Add menu
      </SoftButton>
      {isAddMenusOpen && (
        <AddMenusToMenuSetModal
          open
          menuSet={menuSet}
          closeModal={() => setIsAddMenusOpen(false)}
          onAddMenus={addMenus}
          isSubmitting={isEditingLocked}
        />
      )}
      <div>
        {menus.map((menu) => (
          <MenuSetMenu
            key={menu.menu_id}
            menu={menu}
            menuIds={menuIds}
            updateMenuSet={updateMenuSet}
            isEditingLocked={isEditingLocked || isTrayOpen}
            showRanges={rangesEnabled}
          />
        ))}
      </div>
      <DeleteEntityPrompts
        entityLabel="menu set"
        entityName={menuSet.name}
        unaffectedItems="menu"
        deletePrompt={deletePrompt}
      />
    </div>
  );
};

export default MenuSet;
